import React, { useEffect, useRef } from 'react';

/**
 * Modal
 *
 * On desktop: centered dialog with backdrop
 * On mobile:  bottom sheet that slides up (iOS-style)
 *
 * Usage:
 *   <Modal open={open} onClose={() => setOpen(false)} title="Edit User">
 *     <p>Content here</p>
 *     <Modal.Footer>
 *       <Button variant="secondary" onClick={onClose}>Cancel</Button>
 *       <Button variant="primary" onClick={onSave}>Save</Button>
 *     </Modal.Footer>
 *   </Modal>
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',       // sm | md | lg | xl
  hideClose = false,
  children,
}) {
  const overlayRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const sizeClasses = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
  };

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(2px)',
        zIndex: 'var(--z-modal)',
        animation: 'fadeIn 150ms ease',
      }}
    >
      <div
        className={[
          'bg-white w-full',
          // Mobile: full-width bottom sheet with rounded top corners
          'rounded-t-[var(--radius-2xl)] sm:rounded-[var(--radius-xl)]',
          // Desktop: centered, max-width constrained
          sizeClasses[size],
          'shadow-[var(--shadow-xl)]',
          'flex flex-col',
          // Mobile slides up, desktop fades in
          'sm:animate-none',
        ].join(' ')}
        style={{ animation: 'slideUp 250ms cubic-bezier(0.4, 0, 0.2, 1)' }}
      >
        {/* Handle bar — mobile only */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-[var(--color-border-strong)]" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between px-5 sm:px-6 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b border-[var(--color-border)]">
          <div>
            <h3 className="text-sm font-semibold text-[var(--color-text-primary)] leading-tight">
              {title}
            </h3>
            {description && (
              <p className="text-xs text-[var(--color-text-muted)] mt-0.5">{description}</p>
            )}
          </div>
          {!hideClose && (
            <button
              onClick={onClose}
              className="ml-4 shrink-0 h-7 w-7 flex items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface)] transition-colors"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Body */}
        <div className="px-5 sm:px-6 py-4 sm:py-5 overflow-y-auto flex-1 space-y-4">
          {children}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/**
 * Modal.Footer — sticky footer for action buttons
 * Renders inside the modal, outside the scrollable body
 */
Modal.Footer = function ModalFooter({ children, align = 'right' }) {
  const alignClass = {
    right:   'justify-end',
    left:    'justify-start',
    center:  'justify-center',
    between: 'justify-between',
  }[align];

  return (
    <div className={[
      'flex flex-col-reverse sm:flex-row gap-2.5',
      'px-5 sm:px-6 py-4 sm:py-4',
      'border-t border-[var(--color-border)]',
      alignClass,
    ].join(' ')}>
      {children}
    </div>
  );
};

export default Modal;
